import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, HostBinding, Input, Output, ViewChild } from '@angular/core';
import { environment } from 'environments/environment';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { FileStorageService, IFileInfo, UploadStatus } from 'services/file-storage.service';
import { NotificationService } from 'services/notification.service';
import { DragUploaderComponent } from './drag-uploader';
import { TranslatePipe } from 'pipes/translate.pipe';

export class UploadedFile {
	fileReference?: IFileInfo;
	status: UploadStatus = UploadStatus.InProgress;
	name: string;
	filePath?: string;
	fileData?: File;
	progress = 0;

	constructor(name: string, fileData?: File) {
		this.name = name;
		this.fileData = fileData;
	}
}

@Component({
	selector: 'file-upload',
	templateUrl: './file-upload.html',
	styleUrl: './file-upload.scss',
	imports: [DragUploaderComponent, TranslatePipe]
})
export class FileUploadComponent {

	@Input() uploadedFiles: UploadedFile[] = [];
	@Input() accept: string = '';
	@Input() maxFiles: number = 0;
	@Input() uploadUrl: string = '/api/upload';
	@Input() removeUrl: string = '/api/upload';
	@Input() finalizeUrl?: string;
	@Input() label: string = '';
	@Input() hideUploadedFiles = false;
	@Input() currentFolder?: string;
	@Input() batchSize = 5;

	@HostBinding('class.loading') busy = false;
	@HostBinding('class.initializing') initializingUpload = false;
	@HostBinding('class.finalizing') finalizingUpload = false;
	@Output() busyChange = new EventEmitter<boolean>();
	@Output() filesChanged = new EventEmitter<UploadedFile[]>();

	@ViewChild(DragUploaderComponent) dragUploader?: DragUploaderComponent;

	public uploading: UploadedFile[] = [];
	public get currentFiles() {
		return this.uploadedFiles.length + this.uploading.length;
	}

	constructor(private readonly http: HttpClient, private readonly fileStorageService: FileStorageService, private readonly notificationService: NotificationService) {
	}

	removeFile(file: UploadedFile) {
		if (!file.fileReference) return;
		this.http.delete(environment.gatewayUrl + this.removeUrl + '/' + file.fileReference.id).subscribe(() => {
			this.uploadedFiles.splice(this.uploadedFiles.indexOf(file), 1);
			this.filesChanged.emit(this.uploadedFiles);
		});
	}

	private async uploadFile(uploadingFile: UploadedFile) {

		if (!uploadingFile.fileData || !uploadingFile.fileReference?.uploadUrl) {
			uploadingFile.status = UploadStatus.Error;
			return;
		}

		try {

			const upload = this.fileStorageService.uploadFile(uploadingFile.fileReference.uploadUrl, uploadingFile.fileData);
			upload.subscribe(progress => {
				uploadingFile.progress = Math.round(100 * progress.uploaded / progress.total);
			});
			await lastValueFrom(upload);

			this.uploading.splice(this.uploading.indexOf(uploadingFile), 1);
			this.uploadedFiles.push(uploadingFile);

			uploadingFile.status = UploadStatus.Done;
			this.filesChanged.emit(this.uploadedFiles);
		}
		catch (error) {
			uploadingFile.status = UploadStatus.Error;
			console.error(error);
			if (error instanceof Error) this.notificationService.error('error.upload', error.message);
		}
	}

	private getFileNameWithFolder(file: UploadedFile) {
		return this.currentFolder ? `${this.currentFolder}${file.name}` : file.name;
	}

	async uploadFiles(fileInfos: { file: File, name: string; }[]) {
		if (!fileInfos) return;
		
		let files = fileInfos.map(f => new UploadedFile(f.name, f.file));
		if (this.maxFiles > 0) {
			const remainingFiles = this.maxFiles - this.currentFiles;
			if (remainingFiles <= 0) return;
			files = files.slice(0, remainingFiles);
		}

		this.busyChange.emit(this.busy = true);
		this.initializingUpload = true;
		this.uploading.push(...files);

		const fileReferences = await firstValueFrom(this.http.post<IFileInfo[]>(
			environment.gatewayUrl + this.uploadUrl,
			files.map(f => this.getFileNameWithFolder(f))));

		this.initializingUpload = false;
		for (const file of files) {
			file.fileReference = fileReferences?.find(fr => fr.fileName == this.getFileNameWithFolder(file));
			if (!file.fileReference || !file.fileReference.uploadUrl) file.status = UploadStatus.Error;
			file.filePath = file.fileReference?.fileName;
		}

		const uploadBuffer = files.filter(f => f.status == UploadStatus.InProgress);
		const parallelUpload = async () => {
			// Function which uploads only one file at a time, but multiple can run asynchronously since they remove the uploaded file from the same buffer
			while (true) {
				const file = uploadBuffer.shift();
				if (!file) break;
				await this.uploadFile(file);
			}
		};
		const uploadJobs: Promise<void>[] = [];
		for (let i = 0; i < this.batchSize; i++) uploadJobs.push(parallelUpload());
		await Promise.all(uploadJobs);

		this.finalizingUpload = true;
		if (this.finalizeUrl) await firstValueFrom(this.http.post(`${environment.gatewayUrl}${this.finalizeUrl}`, files.filter(f => f.status == UploadStatus.Done).map(f => f.fileReference?.id)));
		this.finalizingUpload = false;
		this.busyChange.emit(this.busy = false);
	}
}
